import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const STATUS_KLEUREN = {
  concept:     { bg: '#fef3c7', fg: '#92400e', label: '✏️ Concept' },
  ingediend:   { bg: '#dbeafe', fg: '#1e40af', label: '📤 Ingediend' },
  goedgekeurd: { bg: '#d1fae5', fg: '#065f46', label: '✅ Goedgekeurd' },
  afgewezen:   { bg: '#fee2e2', fg: '#991b1b', label: '❌ Afgewezen' }
};

const SubmissionsContextList = ({ werkorderId, projectId, klantId }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    if (werkorderId) params.set('werkorder_id', werkorderId);
    if (projectId)   params.set('project_id', projectId);
    if (klantId)     params.set('klant_id', klantId);

    if (params.toString() === '') {
      setLoading(false);
      return;
    }

    setLoading(true);
    api.get(`/form-submissions/by-context?${params.toString()}`)
      .then(r => {
        if (cancelled) return;
        setSubmissions(r.data?.submissions || []);
      })
      .catch(e => {
        if (cancelled) return;
        // 403 (geen forms-licentie) is geen "error", gewoon niets tonen
        if (e.response?.status !== 403) {
          setError(e.response?.data?.message || 'Kon formulieren niet laden');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [werkorderId, projectId, klantId]);

  const fmtDate = (d) => d ? new Date(d).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const downloadPdf = (submissionId) => {
    const token = localStorage.getItem('token');
    fetch(`/api/form-submissions/${submissionId}/pdf`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (!res.ok) throw new Error('PDF niet beschikbaar');
      return res.blob();
    }).then(blob => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    }).catch(err => alert(err.message));
  };

  const downloadExcel = (submissionId) => {
    const token = localStorage.getItem('token');
    fetch(`/api/form-submissions/${submissionId}/excel`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (!res.ok) throw new Error('Excel niet beschikbaar');
      return res.blob();
    }).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submission-${submissionId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }).catch(err => alert(err.message));
  };

  if (loading) return <div style={{ color: '#64748b', fontSize: 13, padding: 12 }}>Formulieren laden...</div>;
  if (error)   return <div style={{ color: '#991b1b', fontSize: 13, padding: 12 }}>⚠️ {error}</div>;
  if (submissions.length === 0) {
    return <div style={{ color: '#64748b', fontSize: 13, padding: 12, fontStyle: 'italic' }}>Nog geen formulieren gekoppeld.</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
            <th style={{ padding: 8, borderBottom: '2px solid #e2e8f0' }}>Template</th>
            <th style={{ padding: 8, borderBottom: '2px solid #e2e8f0' }}>Status</th>
            <th style={{ padding: 8, borderBottom: '2px solid #e2e8f0' }}>Ingevuld door</th>
            <th style={{ padding: 8, borderBottom: '2px solid #e2e8f0' }}>Datum</th>
            <th style={{ padding: 8, borderBottom: '2px solid #e2e8f0' }}>Handtek.</th>
            <th style={{ padding: 8, borderBottom: '2px solid #e2e8f0', textAlign: 'right' }}>Acties</th>
          </tr>
        </thead>
        <tbody>
          {submissions.map(s => {
            const kleur = STATUS_KLEUREN[s.status] || { bg: '#f1f5f9', fg: '#475569', label: s.status };
            const isReadonly = s.status !== 'concept';
            return (
              <tr key={s.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: 8 }}>
                  <strong>{s.template_naam}</strong>
                  <div style={{ color: '#64748b', fontSize: 11 }}>#{s.id}</div>
                </td>
                <td style={{ padding: 8 }}>
                  <span style={{ background: kleur.bg, color: kleur.fg, padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                    {kleur.label}
                  </span>
                </td>
                <td style={{ padding: 8 }}>{s.ingevuld_door_naam || '-'}</td>
                <td style={{ padding: 8 }}>{fmtDate(s.ingediend_op || s.created_at)}</td>
                <td style={{ padding: 8, textAlign: 'center' }}>
                  {parseInt(s.aantal_handtekeningen, 10) > 0 ? `✍️ ${s.aantal_handtekeningen}` : '-'}
                </td>
                <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => navigate(`/forms/invullen/${s.id}`)}
                    style={{ padding: '4px 8px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}
                  >
                    {isReadonly ? '👁️ Open' : '✏️ Verder'}
                  </button>
                  {isReadonly && s.gegenereerde_pdf_pad && (
                    <button
                      onClick={() => downloadPdf(s.id)}
                      style={{ padding: '4px 8px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}
                      title="PDF openen"
                    >
                      📄
                    </button>
                  )}
                  {isReadonly && (
                    <button
                      onClick={() => downloadExcel(s.id)}
                      style={{ padding: '4px 8px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                      title="Excel downloaden"
                    >
                      📊
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SubmissionsContextList;
